import React, { useState, useEffect, useRef } from "react";
import HorseService from "../../Services/horseService";
import styles from "./Horses.module.css";
import i18n from "../../Services/i18n";
import { useToasts } from "../../LayoutComponents/ToastContext";
import { FiX } from "react-icons/fi";

import HorseInfoTab from "../../Components/HorseTabs/HorseInfoTab";
import HorseNotesTab from "../../Components/HorseTabs/HorseNotesTab";
import HorseMediasTab from "../../Components/HorseTabs/HorseMediasTab";
import HorseServicesTab from "../../Components/HorseTabs/HorseServicesTab";
import HorseNotificationsTab from "../../Components/HorseTabs/HorseNotificationsTab";
import { useHorses } from "../../Context/HorsesContext";
import { useLocation } from "react-router-dom";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import ListCardContainer from "../../Components/ListCard/ListCardContainer";
import HorseBodyMapTab from "../../Components/HorseTabs/HorseBodyMapTab";

const Tabs = {
  INFO: "Infos",
  SERVICES: "Services reçus",
  MEDIA: "Médias",
  BODYMAP: "BodyMaps",
  NOTES: "Notes",
  NOTIFICATIONS: "Notifications",
};

const Horses = () => {
  const [isLoadingHorseDetail, setIsLoadingHorseDetail] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState();
  const [selectedHorseDetail, setSelectedHorseDetail] = useState();
  const [filterText] = useState("");
  const [sortDirection] = useState("asc");
  const [sortField] = useState("name");
  const [activeTab, setActiveTab] = useState(Tabs.INFO);
  const [isLocked, setIsLocked] = useState(false);

  const isArchived = selectedHorseDetail?.isArchived === true;
  const location = useLocation();
  const { paramHorseId } = location.state || {};
  const { paramHorseClientId } = location.state || {};
  const { paramHorseTab } = location.state || {};
  const [horseClientExternalId, setHorseClientExternalId] = useState(paramHorseClientId);

  const { addToast } = useToasts();
  const { horsesData, isFetchingHorses, fetchHorses } = useHorses();
  const horseListRef = useRef({});

  useEffect(() => {
    if (paramHorseTab && Object.values(Tabs).includes(paramHorseTab)) {
      setActiveTab(paramHorseTab);
    }

    if (paramHorseId && horsesData.length > 0) {
      const horse = horsesData.find((c) => c.externalId === paramHorseId);

      if (horse) {
        handleSelectHorse(horse);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramHorseId, paramHorseTab, horsesData]);

  const refreshHorseList = async (horseExternalId) => {
    try {
      await fetchHorses();

      if (horseExternalId) {
        const horseToRefresh =
          selectedHorse?.externalId === horseExternalId
            ? selectedHorse
            : { externalId: horseExternalId };

        await handleSelectHorse(horseToRefresh, true);
      } else {
        setSelectedHorse(undefined);
        setSelectedHorseDetail(undefined);
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const handleSelectHorse = async (horse, refreshHorse = false) => {
    try {
      if (!refreshHorse && selectedHorse && horse.externalId === selectedHorse.externalId) {
        return;
      }

      setIsLoadingHorseDetail(true);
      const horseDetail = await HorseService.GetDetailHorse(horse.externalId);

      if (!refreshHorse) {
        const criticalNotes = horseDetail?.notes?.filter((e) => e.isCritical) ?? [];
        if (criticalNotes.length) {
          criticalNotes.forEach((e) => {
            addToast(e.note, e.externalId, "CUSTOM", true, e.color);
          });
        }
      }

      setSelectedHorseDetail(horseDetail);
      setSelectedHorse(horse);

      horseListRef.current.scrollToItem(horse.externalId);
      setIsLoadingHorseDetail(false);
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const getHeaderTitleName = () => {
    if (horseClientExternalId && selectedHorse) {
      return (
        <>
          {" "}
          <span className={styles.headerTitleName}>{selectedHorse.clientCardName}</span>
        </>
      );
    }
  };

  const getFilteredAndSortedHorses = () => {
    return horsesData
      .filter(
        (horse) =>
          horse.cardName.toLowerCase().includes(filterText.toLowerCase()) &&
          (!horseClientExternalId || horse.clientExternalId === horseClientExternalId),
      )
      .sort((a, b) => {
        let result =
          sortField === "name"
            ? a.cardName.localeCompare(b.cardName)
            : new Date(a.createdOn) - new Date(b.createdOn);
        return sortDirection === "asc" ? result : -result;
      });
  };

  const renderTabs = () => (
    <div className={styles.tabs}>
      {Object.values(Tabs).map((tab) => {
        const isDisabled = isLocked;
        return (
          <div
            key={tab}
            style={{
              transition:
                "background-color 0.2s ease, " +
                "border-bottom 0.2s ease,  border-top 0.2s ease, border-left 0.2s ease,  border-right 0.2s ease, " +
                "font-weight 0.2s ease, padding 0.2s ease",
            }}
            className={
              activeTab === tab ? styles.activeTab : isDisabled ? styles.tabDisabled : styles.tab
            }
            onClick={() => !isDisabled && setActiveTab(tab)}>
            {tab}
          </div>
        );
      })}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case Tabs.INFO:
        return (
          <div>
            {selectedHorse && (
              <HorseInfoTab
                horse={selectedHorseDetail}
                reloadDetailHorse={(horse) => handleSelectHorse(horse)}
                refreshHorseList={refreshHorseList}
                lockParent={(v) => setIsLocked(v)}
              />
            )}
          </div>
        );
      case Tabs.NOTES:
        return (
          <div>
            {selectedHorse && (
              <HorseNotesTab
                horse={selectedHorseDetail}
                reloadDetailHorse={(horse, refresh) => handleSelectHorse(horse, refresh)}
              />
            )}
          </div>
        );
      case Tabs.SERVICES:
        return (
          <div>
            {selectedHorse && (
              <HorseServicesTab
                horse={selectedHorseDetail}
                reloadDetailHorse={(horse, refresh) => handleSelectHorse(horse, refresh)}
              />
            )}
          </div>
        );
      case Tabs.MEDIA:
        return (
          <div>
            {selectedHorse && (
              <HorseMediasTab
                horse={selectedHorseDetail}
                reloadDetailHorse={(horse, refresh) => handleSelectHorse(horse, refresh)}
              />
            )}
          </div>
        );
      case Tabs.BODYMAP:
        return (
          <div>
            {selectedHorse && (
              <HorseBodyMapTab
                horse={selectedHorseDetail}
                reloadDetailHorse={(horse, refresh) => handleSelectHorse(horse, refresh)}
                lockParent={(v) => setIsLocked(v)}
              />
            )}
          </div>
        );
      case Tabs.NOTIFICATIONS:
        return <div>{selectedHorse && <HorseNotificationsTab horse={selectedHorseDetail} />}</div>;
      default:
        return <div></div>;
    }
  };

  const filteredHorses = getFilteredAndSortedHorses();

  const renderHorseDetail = () => {
    if (!selectedHorse && isLoadingHorseDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    if (!selectedHorse && !isLoadingHorseDetail) {
      return <div className={styles.centerMessage}>{i18n.t("HorsePage.PleaseSelect")}</div>;
    }

    if (isLoadingHorseDetail) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        </div>
      );
    } else {
      const blockInteraction = isArchived && activeTab !== Tabs.INFO;

      return (
        <div
          className={`${styles.tabContent} ${blockInteraction ? styles.archivedReadOnly : ""}`}
          inert={blockInteraction}>
          {renderTabContent()}
        </div>
      );
    }
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.clientsListSection} ${isLocked ? styles.clientsListSectionDisabled : ""}`}>
        <ListCardContainer
          headerLabel={i18n.t("SideBar.MenuHorses")}
          headerSubLabel={getHeaderTitleName()}
          clearHeaderFilter={() => setHorseClientExternalId(null)}
          isLoading={isFetchingHorses}
          dataItems={filteredHorses ?? []}
          onSelect={handleSelectHorse}
          selectedItemExternalId={selectedHorse?.externalId}
          type="horse"
          ref={horseListRef}
          storageId={"HorsePage.Horse"}
          hideEmptyHorseCard={true}
          refresh={() => {
            refreshHorseList();
          }}
        />
      </div>
      <div className={`${styles.clientInfoSection} ${!selectedHorse ? styles.faded : ""}`}>
        <div
          className={`${styles.boardSectionHeader} ${
            isArchived ? styles.boardSectionHeaderArchived : ""
          }`}>
          <div className={styles.headerContainer}>
            {selectedHorse ? (
              <>
                <span className={styles.headerTitle} title={selectedHorse?.cardName ?? ""}>
                  {selectedHorse?.cardName ?? ""}
                </span>

                {isArchived && (
                  <span className={styles.archivedBadge}>{i18n.t("HorsePage.Archived")}</span>
                )}
              </>
            ) : (
              <span>...</span>
            )}
          </div>
          {isArchived && (
            <div className={styles.archivedNotice}>{i18n.t("HorsePage.ArchivedReadOnly")}</div>
          )}
        </div>
        {renderTabs()}

        {renderHorseDetail()}
      </div>
    </div>
  );
};

export default Horses;
